import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RecipeType } from '@kemu-io/kemu-types';
import Icon from '@ant-design/icons';
import { Dropdown, Button, Typography, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import EditableHeading from '../form-control/editableHeading/editableHeading';
import {
	setRecipeName,
	currentRecipeTitle,
	selectCurrentRecipe,
} from '../../features/Workspace/workspaceSlice';
import { useTranslation } from '../../common/hooks/index';
import { ReactComponent as CloudIcon } from './cloud-storage.svg';
import { ReactComponent as DiskIcon } from './file-storage.svg';
import styles from './RecipeWorkspaceTitle.module.css';
import { selectRecipeDownloadDetails } from '../../features/interface/interfaceSlice';
import { downloadRecipeAction } from '../../features/interface/reducers/downloadRecipeReducer';
import useAlert from '../alert/useAlert';
import useNavigateWithSearch from '../../common/hooks/useNavigateWithSearch';
import { openCheckoutModalAction } from '@src/app/reducers/checkout/checkoutSlice';
import { StarOutlined } from '@ant-design/icons';
export interface MenuInfo {
	key: string;
	keyPath: string[];
	/** @deprecated This will not support in future. You should avoid to use this */
	item: React.ReactInstance;
	domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
}

const RecipeWorkspaceTitle = (): React.JSX.Element => {
	const dispatch = useDispatch();
	const t = useTranslation('Recipe');
	const Alert = useAlert();
	const navigateWithSearch = useNavigateWithSearch();
	const defaultRecipeTitle = t('DefaultTitle', 'Untitled Recipe');
	const recipeTitle = useSelector(currentRecipeTitle);
	const currentRecipe = useSelector(selectCurrentRecipe);
	const downloadDetails = useSelector(selectRecipeDownloadDetails);
	const moreVersionsAvailable = currentRecipe.moreVersionsAvailable;
	const isCloudRecipe = currentRecipe.type === RecipeType.Browser && currentRecipe.entityInfo?.dbId;

	const handleTitleChange = (text: string) => {
		dispatch(setRecipeName(text));
	};

	const handleUpgrade = useCallback(() => {
    dispatch(openCheckoutModalAction());
  }, [dispatch]);
	
	const renderIcon = () => {
		// Don't show any icon if it's a default recipe that hasn't been saved yet
		if (currentRecipe.isDefault) {
			return null;
		}

		// Show cloud icon for browser recipes that have been saved to the cloud
		if (currentRecipe.type === RecipeType.Browser && currentRecipe.entityInfo?.dbId) {
			return <Icon component={CloudIcon} className={styles.StorageIcon} />;
		}

		// Show disk icon for desktop recipes
		if (currentRecipe.type === RecipeType.Desktop) {
			return <Icon component={DiskIcon} className={styles.StorageIcon} />;
		}

		return null;
	};

	const isLoading = downloadDetails?.status === 'loading';

	const versionItems = useMemo((): MenuProps['items'] => {
		const history = currentRecipe.versionHistory || [];
		if (!history.length) { return []; }

		const sorted = [...history].sort((a, b) => {
			const ad = new Date(a.createdAt).getTime();
			const bd = new Date(b.createdAt).getTime();
			return bd - ad; // newest first
		});

		const items = sorted.map((item) => ({
			key: `v-${item.version}`,
			disabled: isLoading || item.version === currentRecipe.currentVersion,
			label: (
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<strong>v{item.version}</strong>
						{item.version === currentRecipe.currentVersion && (
							<span style={{ fontSize: 11, color: '#52c41a' }}>{t('CurrentVersion', 'current')}</span>
						)}
					</div>
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						{new Date(item.createdAt).toLocaleString()}
					</Typography.Text>
					{item.comments && (
						// <Tooltip title={item.comments}>
							<Typography.Text ellipsis style={{ maxWidth: 260 }}>
								{item.comments}
							</Typography.Text>
						// </Tooltip>
					)}

				</div>
			),
		}))
		
		if(moreVersionsAvailable) {
			items.push({
				key: 'more-versions',
				disabled: false,
				// @ts-expect-error bad typings
				className: styles.MoreVersionsMenuItem,
				['cta-menu-item']: 'true',
				label: (
					<div className={styles.MoreVersionsAvailable} onClick={handleUpgrade}>
						<div className={styles.Icon}><StarOutlined /></div>
						<div className={styles.Text}>
							<div className={styles.Title}>{t('MoreVersionsAvailable.Title')}</div>
							<div className={styles.Description}>{t('MoreVersionsAvailable.Description')}</div>
						</div>
					</div>
				),
			});
		}

		return items;
	}, [currentRecipe.versionHistory, currentRecipe.currentVersion, isLoading, t]);

	const onSelectVersion = useCallback((info: { key: string }) => {
		const v = Number(String(info.key).replace('v-', ''));
		if (!Number.isFinite(v) || v === currentRecipe.currentVersion) { return; }
		if (!currentRecipe.entityInfo?.dbId) { return; }

		dispatch(downloadRecipeAction({
			recipeId: currentRecipe.entityInfo.dbId,
			version: v,
			alert: Alert,
			navigate: navigateWithSearch,
		}));
	}, [dispatch, currentRecipe.currentVersion, currentRecipe.entityInfo?.dbId, Alert, navigateWithSearch]);

	return (
		<div className={styles.TitleContainer}>
			{renderIcon()}
			<EditableHeading
				onEditFinished={handleTitleChange}
				tag="h1"
				text={recipeTitle || defaultRecipeTitle}
				className={styles.Header}
			/>
			{currentRecipe.versionHistory?.length > 0 && isCloudRecipe && (
				<Dropdown
					// open
					menu={{ items: versionItems, onClick: onSelectVersion }}
					rootClassName={styles.RootMenu}
				>
					<Button type="text" className={styles.VersionButton} aria-label={t('SelectVersion', 'Select version')}>
						<span className={styles.VersionTrigger}>
							{currentRecipe.currentVersion ? (
								<span className={styles.VersionBadge}>v{currentRecipe.currentVersion}</span>
							) : null}
						</span>
					</Button>
				</Dropdown>
			)}
		</div>
	);
};

export default RecipeWorkspaceTitle;
